<template>
  <v-dialog
    :model-value="modelValue"
    fullscreen
    scrollable
    :scrim="true"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <div class="bm-overlay" data-testid="release-overlay" role="dialog" :aria-label="`Release batch ${batch.id}`">
      <header class="bm-overlay__head">
        <div class="bm-overlay__headings">
          <h2 class="bm-overlay__title">Release batch {{ batch.id }}</h2>
          <p class="bm-overlay__sub">
            Read this back before you commit. This is the step that sends the batch to the decorator.
          </p>
        </div>
        <button type="button" class="bm-iconbtn" data-testid="release-close" aria-label="Close" @click="close">
          <i class="material-icons" aria-hidden="true">close</i>
        </button>
      </header>

      <div class="bm-overlay__body">
        <!--
          The read-back. ADR 0066 (3): the review restates order count, the
          value of the batch, and the receiving decorator BEFORE the confirm
          control is reachable.

          TWO DIFFERENT NUMBERS, labelled as such (#630). "Apparel value" is
          the goods (`orderTotal`, cart lines less the frozen discount). "Charged
          to cards" is what release charges: the server's per-order
          `cardCharge` (apparel + frozen tax, shipping, fees), summed. The
          browser never computes money: an order without a served figure shows
          none, and the batch total is withheld unless every order has one.
          #2790's defect was a control offering to "charge $631.00" when nothing
          was charged; the charge is real now (#3184), cards are still charged
          per order and some may decline, so the figure is stated as what each
          card is charged, not as a promise of what the batch collects.
        -->
        <div class="bm-facts">
          <div class="bm-fact" data-testid="review-fact-orders">
            <i class="material-icons bm-fact__icon" aria-hidden="true">receipt_long</i>
            <div>
              <div class="bm-fact__label">Orders being sent</div>
              <div class="bm-fact__value">{{ chargeable.length }} orders</div>
              <div class="bm-fact__sub">{{ totalItems }} items in total</div>
            </div>
          </div>
          <div class="bm-fact" data-testid="review-fact-value">
            <i class="material-icons bm-fact__icon" aria-hidden="true">checkroom</i>
            <div>
              <div class="bm-fact__label">Apparel value being sent</div>
              <div class="bm-fact__value">{{ money(total) }}</div>
              <div class="bm-fact__sub">The goods only, before tax, shipping and fees</div>
            </div>
          </div>
          <div class="bm-fact bm-fact--danger" data-testid="review-fact-charged">
            <i class="material-icons bm-fact__icon" aria-hidden="true">credit_card</i>
            <div>
              <div class="bm-fact__label">Charged to cards on release</div>
              <div v-if="chargeTotal !== null" class="bm-fact__value">{{ money(chargeTotal) }}</div>
              <div v-else class="bm-fact__value bm-fact__value--na">Not available</div>
              <div class="bm-fact__sub">
                The saved card on each order is charged its order total on release
              </div>
            </div>
          </div>
          <!-- Absent rather than blank when the decorator is unknown. -->
          <div v-if="decoratorText" class="bm-fact" data-testid="review-fact-decorator">
            <i class="material-icons bm-fact__icon" aria-hidden="true">local_shipping</i>
            <div>
              <div class="bm-fact__label">Goes to</div>
              <div class="bm-fact__value">{{ decoratorText }}</div>
              <div class="bm-fact__sub">Production starts on receipt</div>
            </div>
          </div>
        </div>

        <!--
          Amber, not red: this is a consequence statement on a deliberate
          action, not an error.

          THE CHARGE AND THE IRREVERSIBILITY ARE ONE SENTENCE, deliberately.
          Split across two, a reader takes in one and misses the other -- and
          the two together are the whole reason this screen exists: money moves
          on a click that cannot be taken back.

          "A declining card" is BACK, and this time it is real. #2790 removed
          it because nothing was charged and so nothing could decline; orders
          were held back for being pre-payment. #3184 restores it because
          `make_charge_fn()` really does charge off-session and a card really
          can decline -- `batch_charge.py` fails closed, the order is held back
          with the decline reason, and `ensure_batch_for_held_back_order`
          (batch_lifecycle.py:462) moves it into the next batch.
        -->
        <div class="bm-noundo" data-testid="release-no-undo">
          <i class="material-icons bm-noundo__icon" aria-hidden="true">report_problem</i>
          <div>
            <b>There is no undo: releasing charges the saved card on each order, and the charge cannot be reversed here.</b>
            Once released, this batch cannot be reopened, edited or cancelled, and the decorator begins work.
            An order whose card declines is held back and the rest still goes.
          </div>
        </div>

        <div class="bm-sectionlabel">Every order in this batch</div>
        <div class="bm-orderlist">
          <div
            v-for="o in batch.orders"
            :key="o.id"
            class="bm-orderlist__row"
            :data-testid="`review-${o.id}`"
          >
            <div class="bm-orderlist__who">
              <span>{{ o.shopper }}</span>
              <span class="bm-orderlist__meta"> &middot; {{ itemCount(o) }} {{ itemCount(o) === 1 ? 'item' : 'items' }}</span>
            </div>
            <div class="bm-orderlist__amt">
              <div v-if="o.held" :data-testid="`review-charge-${o.id}`">Held back</div>
              <div v-else-if="o.cardCharge !== null" :data-testid="`review-charge-${o.id}`">
                {{ money(o.cardCharge) }} charged
              </div>
              <div v-else :data-testid="`review-charge-${o.id}`">Charge unavailable</div>
              <div class="bm-orderlist__meta">{{ money(orderTotal(o)) }} apparel</div>
            </div>
          </div>
          <div class="bm-orderlist__total">
            <span>Apparel value at release</span>
            <span data-testid="review-total">{{ money(total) }}</span>
          </div>
          <div v-if="chargeTotal !== null" class="bm-orderlist__total">
            <span>Total charged to cards on release</span>
            <span data-testid="review-charge-total">{{ money(chargeTotal) }}</span>
          </div>
        </div>

        <p class="bm-freight">
          <template v-if="freightCovered">
            {{ money(batch.blanks) }} of blank goods &mdash; above the
            {{ money(freightThreshold) }} free-freight line, so freight is covered.
          </template>
          <template v-else-if="freightThreshold !== null">
            {{ money(batch.blanks) }} of blank goods &mdash; below the
            {{ money(freightThreshold) }} free-freight line, so freight comes out
            of your margin.
          </template>
          <template v-else>
            {{ money(batch.blanks) }} of blank goods.
          </template>
        </p>
      </div>

      <footer class="bm-overlay__foot">
        <!--
          While a release is in flight the confirm control is REMOVED, not
          disabled: a second click would submit the batch twice.
        -->
        <div v-if="busy" class="bm-busy" data-testid="release-busy">
          <span class="bm-spinner" />
          <span>Sending to {{ batch.decoratorTarget }}&hellip;</span>
        </div>
        <template v-else>
          <div class="bm-confirmrow">
            <!--
              The amount is IN the label, so the last thing read before the
              click is the consequence. A typed confirmation word was tried
              and cut: friction on the phone path without comprehension.
            -->
            <button
              type="button"
              class="bm-btn bm-btn--danger bm-btn--lg"
              data-testid="confirm-release"
              @click="$emit('confirm')"
            >
              <i class="material-icons" aria-hidden="true">send</i>
              Release {{ chargeable.length }} {{ chargeable.length === 1 ? 'order' : 'orders' }} to {{ batch.decoratorTarget }}
            </button>
            <!--
              The last thing read before the click. Charge and irreversibility
              share the sentence for the same reason as the block above.

              NO AMOUNT HERE. "Release and charge $631.00" was #2790's exact
              defect, and the objection outlives that bug: the total on screen
              is the value being sent, cards are charged per order, and a
              decline makes any promised figure wrong after the fact.

              "The saved card on each order", never "your card" -- the
              distributor is not the payer.
            -->
            <div class="bm-confirmrow__notes">
            <p class="bm-confirmrow__note" data-testid="release-confirm-note">
              Sends the batch to the decorator and charges the saved card on each order &mdash;
              this cannot be undone.
            </p>
            <!--
              Prepares the distributor for a decline BEFORE the click, because
              a decline is the DESIGNED path, not a failure. Met for the first
              time on the post-release results screen, it reads as something
              having gone wrong. Every clause is verified in code:
              batch_charge.py:250 notifies the shopper via
              notify_charge_failure, and the order stays in this batch (it is
              not sent) until the shopper pays, then joins the next release
              (batch_release.py header, ADR 0066 decision 4).
            -->
            <p class="bm-confirmrow__decline" data-testid="release-decline-note">
              If a card declines, that order does not go out. The shopper is emailed a link to pay, and the order goes out with your next release.
            </p>
            </div>
          </div>
          <div class="bm-dismiss">
            <button type="button" class="bm-btn bm-btn--quiet" data-testid="release-cancel" @click="close">
              Not yet &mdash; keep it in Holding
            </button>
          </div>
        </template>
      </footer>
    </div>
  </v-dialog>
</template>

<script>
/**
 * The release step: a full-screen review that reads the batch back before it
 * lets anyone commit.
 *
 * "The read-back is the safeguard, not the button." Confirming is a single
 * click; the caution lives in what precedes it. This component therefore does
 * NOT own the release call -- it emits `confirm` and lets the panel own the
 * request, so the irreversible action has exactly one implementation.
 *
 * WHAT RELEASE ACTUALLY DOES (#3184) -- verified in the backend, not assumed,
 * because this screen previously described the opposite:
 *
 *   - `batch_management.py` releases with `charge_fn=make_charge_fn()` at all
 *     three call sites (:1170, :1428, :1604); `batch_auto_release.py:362` too.
 *   - `batch_charge.py` makes real off-session `PaymentIntent.create` calls
 *     against each order's saved card.
 *   - There is NO retract path.
 *
 * Until #3184 this screen said "No payment is collected on release" while that
 * button charged shoppers' cards. That is the inverse of #2790 and worse:
 * #2790 over-promised a charge that never came, this under-warned about one
 * that did. The copy here is the warning, so treat it as load-bearing and keep
 * it matched to the backend -- `batch-no-false-charge-claim.spec.js` ratchets
 * it in both directions.
 */
import {
  money,
  batchTotal,
  batchChargeTotal,
  orderTotal,
  itemCount,
  decoratorLabel,
} from "@/services/batchManagementService";

export default {
  name: "ReleaseReview",
  props: {
    modelValue: { type: Boolean, default: false },
    batch: { type: Object, required: true },
    // The store's batching rules. The free-freight line is PER STORE and
    // editable; hardcoding the platform default told a distributor who raised
    // their threshold a number that was not theirs.
    rules: { type: Object, default: () => ({}) },
    busy: { type: Boolean, default: false },
  },
  emits: ["update:modelValue", "confirm"],
  computed: {
    decoratorText() {
      return decoratorLabel(this.batch.decoratorRouting);
    },
    // Orders that will actually be charged. `batch.orders` includes held-back
    // orders, which are NOT charged -- counting them on the commitment screen
    // overstates both the card count and the amount.
    chargeable() {
      return (this.batch.orders || []).filter((o) => !o.held);
    },
    total() {
      return this.chargeable.reduce((s, o) => s + orderTotal(o), 0);
    },
    // What release charges, from the server's per-order figures; null unless
    // every chargeable order has one (#630).
    chargeTotal() {
      return batchChargeTotal(this.batch);
    },
    batchGrandTotal() {
      return batchTotal(this.batch);
    },
    freightThreshold() {
      const t = Number(this.rules && this.rules.threshold);
      return Number.isFinite(t) && t > 0 ? t : null;
    },
    freightCovered() {
      const t = this.freightThreshold;
      return t !== null && Number(this.batch.blanks) >= t;
    },
    totalItems() {
      return this.chargeable.reduce((s, o) => s + itemCount(o), 0);
    },
  },
  methods: {
    money,
    orderTotal,
    itemCount,
    close() {
      this.$emit("update:modelValue", false);
    },
  },
};
</script>

<style scoped>
.bm-overlay {
  background: var(--tf-surface);
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.bm-overlay__head {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 18px 24px;
  border-bottom: 1px solid var(--tf-border);
  flex-shrink: 0;
}

.bm-overlay__headings {
  flex: 1;
  min-width: 0;
}

.bm-overlay__title {
  font-size: 19px;
  font-weight: 500;
  color: var(--tf-fg-1);
  margin: 0;
}

.bm-overlay__sub {
  font-size: 13px;
  color: var(--tf-fg-3);
  margin: 4px 0 0;
  line-height: 1.5;
}

.bm-iconbtn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  color: var(--tf-fg-4);
  min-width: 44px;
  min-height: 44px;
  align-items: center;
  justify-content: center;
}

.bm-overlay__body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 4px 24px 22px;
}

.bm-facts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 26px;
  border-bottom: 1px solid var(--tf-border);
  margin-bottom: 16px;
}

.bm-fact {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  padding: 14px 0;
}

.bm-fact__icon {
  font-size: 22px;
  margin-top: 2px;
  color: var(--tf-primary);
  flex-shrink: 0;
}

.bm-fact--danger .bm-fact__icon,
.bm-fact--danger .bm-fact__value {
  color: var(--tf-error-dark);
}

.bm-fact__label {
  font-size: 10.5px;
  letter-spacing: 1.3px;
  text-transform: uppercase;
  color: var(--tf-fg-quiet);
  font-weight: 500;
}

.bm-fact__value {
  font-size: 22px;
  font-weight: 500;
  color: var(--tf-fg-1);
  margin-top: 2px;
  line-height: 1.25;
}

.bm-fact__value--na {
  font-size: 15px;
  color: var(--tf-fg-3);
}

.bm-fact__sub {
  font-size: 12.5px;
  color: var(--tf-fg-3);
  margin-top: 3px;
  line-height: 1.5;
}

.bm-noundo {
  display: flex;
  gap: 10px;
  background: var(--tf-warning-soft);
  border: 1px solid var(--tf-chip-pending-border);
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 18px;
  font-size: 13px;
  color: var(--tf-chip-pending-fg);
  line-height: 1.55;
}

.bm-noundo__icon {
  font-size: 18px;
  margin-top: 1px;
  flex-shrink: 0;
}

.bm-noundo b {
  font-weight: 500;
}

.bm-sectionlabel {
  font-size: 10.5px;
  letter-spacing: 1.3px;
  text-transform: uppercase;
  color: var(--tf-fg-quiet);
  font-weight: 500;
  margin-bottom: 8px;
}

.bm-orderlist {
  border: 1px solid var(--tf-border);
  border-radius: 10px;
  overflow: hidden;
}

.bm-orderlist__row {
  display: flex;
  gap: 12px;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  font-size: 13.5px;
}

.bm-orderlist__row + .bm-orderlist__row {
  border-top: 1px solid var(--tf-border);
}

.bm-orderlist__meta {
  color: var(--tf-fg-4);
  font-size: 12px;
}

.bm-orderlist__amt {
  font-weight: 500;
  white-space: nowrap;
  text-align: right;
}

.bm-orderlist__total {
  display: flex;
  justify-content: space-between;
  padding: 12px 14px;
  background: var(--tf-surface-light);
  border-top: 1px solid var(--tf-border);
  font-size: 14.5px;
  font-weight: 500;
}

.bm-freight {
  font-size: 12.5px;
  color: var(--tf-fg-quiet);
  margin-top: 12px;
  line-height: 1.55;
}

.bm-overlay__foot {
  flex-shrink: 0;
  border-top: 1px solid var(--tf-border);
  background: var(--tf-surface);
  padding: 16px 24px;
}

.bm-confirmrow {
  display: flex;
  gap: 14px;
  align-items: center;
}

/* Stacks the two notes so the confirm row stays button | text, not a third column. */
.bm-confirmrow__notes {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.bm-confirmrow__note {
  font-size: 12.5px;
  color: var(--tf-fg-3);
  line-height: 1.5;
  margin: 0;
}

/* Quieter than the charge sentence: the decline path is information, not the
   consequence being consented to. */
.bm-confirmrow__decline {
  font-size: 12px;
  color: var(--tf-fg-quiet);
  line-height: 1.5;
  margin: 0;
}

.bm-dismiss {
  margin-top: 12px;
}

.bm-btn {
  border-radius: 9999px;
  font-family: inherit;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: 44px;
  padding: 9px 18px;
  font-size: 13.5px;
  border: 1px solid var(--tf-border-strong);
  background: var(--tf-surface);
  color: var(--tf-fg-2);
}

.bm-btn--lg {
  min-height: 48px;
  padding: 13px 26px;
  font-size: 15px;
}

/* Red: destructive/irreversible. */
.bm-btn--danger {
  background: var(--tf-error);
  border-color: var(--tf-error);
  color: var(--tf-fg-on-primary);
}

.bm-btn--quiet {
  background: transparent;
  border-color: transparent;
  color: var(--tf-primary);
}

.bm-busy {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 52px;
  font-size: 14px;
  color: var(--tf-fg-2);
}

.bm-spinner {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid var(--tf-primary-soft);
  border-top-color: var(--tf-primary);
  display: inline-block;
  animation: bm-spin 800ms linear infinite;
}

@keyframes bm-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Mobile: full-bleed, single column, full-width confirm. */
@media (max-width: 599px) {
  .bm-overlay__head,
  .bm-overlay__body,
  .bm-overlay__foot {
    padding-left: 16px;
    padding-right: 16px;
  }

  .bm-facts {
    grid-template-columns: 1fr;
    gap: 0;
  }

  .bm-confirmrow {
    flex-direction: column;
    align-items: stretch;
    text-align: center;
  }

  .bm-confirmrow .bm-btn {
    width: 100%;
  }
}
</style>
